import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { LuPlaneLanding, LuPlaneTakeoff, LuTriangleAlert, LuLock, LuArrowUpToLine, LuChevronUp, LuChevronDown, LuX } from 'react-icons/lu';

const PX_PER_MINUTE = 12;
const MIN_ETIQUETA_HEIGHT_PX = 28;
const ETIQUETA_STACK_GAP_PX = 4;
/** Deslocamento à direita da borda esquerda do card que fica por cima quando há sobreposição */
const ETIQUETA_OVERLAP_OFFSET_PX = 14;
/** Máximo de camadas sobrepostas antes de empilhar verticalmente */
const MAX_OVERLAP_LAYERS = 6;
const PAST_MINUTES = 120;   // 2 horas para trás
const FUTURE_MINUTES = 360; // 6 horas para a frente
const TOTAL_MINUTES = PAST_MINUTES + FUTURE_MINUTES;
const TOTAL_PX = TOTAL_MINUTES * PX_PER_MINUTE;
const NOW_Y = PAST_MINUTES * PX_PER_MINUTE;

function pad(n) {
  return String(n).padStart(2, '0');
}
/** Formata hora em UTC (régua e relógio) */
function fmtTime(d) {
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

function statusColor(status) {
  switch (status) {
    case 'now':
      return '#00e5ff';
    case 'soon':
      return '#ffaa00';
    case 'past':
      return '#606880';
    default:
      return '#3a5580';
  }
}

/** Cor da borda da etiqueta de alerta conforme o nível de risco */
function riskColor(risk) {
  const r = (risk || 'Médio').toString().toLowerCase();
  if (r.includes('baixo')) return '#22c55e';
  if (r.includes('alto')) return '#ef4444';
  return '#eab308';
}

/** Remove travessões (— ou –) do texto dos alertas: nenhum campo de alerta pode exibir esse caractere */
function semTravessao(texto) {
  if (!texto) return '';
  return String(texto).replace(/[—–]/g, ' ').replace(/\s{2,}/g, ' ').trim();
}

/** Prioridade de risco: Alto > Médio > Baixo */
function riskPriority(risk) {
  const r = (risk || 'Médio').toString().toLowerCase();
  if (r.includes('alto')) return 3;
  if (r.includes('médio') || r.includes('medio')) return 2;
  if (r.includes('baixo')) return 1;
  return 2;
}

/** Cor do indicador de alerta para voo que coincide no tempo com alerta (prioridade: Alto > Médio > Baixo). Retorna null se não houver overlap. */
function flightAlertIndicatorColor(flightTime, alertRows) {
  if (!Array.isArray(alertRows) || alertRows.length === 0) return null;
  let maxPriority = 0;
  let bestRisk = null;
  for (const r of alertRows) {
    const start = r.time.getTime();
    const end = start + (r.durationMinutes ?? 0) * 60000;
    const t = flightTime.getTime();
    if (t >= start && t <= end) {
      const p = riskPriority(r.alertData?.risk);
      if (p > maxPriority) {
        maxPriority = p;
        bestRisk = r.alertData?.risk;
      }
    }
  }
  return bestRisk != null ? riskColor(bestRisk) : null;
}

function buildTicks(now) {
  const ticks = [];
  for (let m = -PAST_MINUTES; m <= FUTURE_MINUTES; m += 15) {
    ticks.push({
      m,
      label: fmtTime(new Date(now.getTime() + m * 60000)),
      y: NOW_Y + m * PX_PER_MINUTE,
    });
  }
  return ticks;
}

/**
 * Obtém detalhes do alerta (voos ou API).
 * @param {Object} row - Row do alerta
 * @param {Map<number, string>|undefined} [equipamentosIdToName] - Mapa id equipamento -> name; se fornecido, exibe nomes em vez de índices
 */
function getDetalhesAlerta(row, equipamentosIdToName, idioma = 'pt') {
  const fim = new Date(row.time.getTime() + (row.durationMinutes || 0) * 60000);
  const nivel = idioma === 'en'
    ? (row.status === 'now' ? 'Current' : row.status === 'soon' ? 'Upcoming' : row.status === 'past' ? 'Past' : 'Expected')
    : (row.status === 'now' ? 'Atual' : row.status === 'soon' ? 'Próximo' : row.status === 'past' ? 'Passado' : 'Previsto');
  const riskEnMap = { Alto: 'High', Médio: 'Medium', Baixo: 'Low' };
  if (row.alertFromApi && row.alertData) {
    let equipamentosStr = '';
    if (row.alertData.equipamentos && row.alertData.equipamentos.length > 0) {
      if (equipamentosIdToName && equipamentosIdToName.size > 0) {
        equipamentosStr = row.alertData.equipamentos
          .map((id) => equipamentosIdToName.get(id) ?? `#${id}`)
          .join(', ');
      } else {
        equipamentosStr = row.alertData.equipamentos.join(', ');
      }
    }
    return {
      titulo: idioma === 'en' ? (row.alertData.title_en || row.alertaEn || row.alerta) : row.alerta,
      inicio: row.time,
      fim,
      duracaoMin: row.durationMinutes ?? 0,
      descricao: semTravessao(idioma === 'en' ? (row.alertData.description_en || row.alertData.description) : row.alertData.description),
      nivel: semTravessao((idioma === 'en' ? (row.alertData.risk_en || riskEnMap[row.alertData.risk] || row.alertData.risk) : row.alertData.risk) ? `${(idioma === 'en' ? (row.alertData.risk_en || riskEnMap[row.alertData.risk] || row.alertData.risk) : row.alertData.risk)} (${nivel})` : nivel),
      equipamentos: equipamentosStr,
    };
  }
  return {
    titulo: idioma === 'en' ? (row.alertaEn || row.alerta) : row.alerta,
    inicio: row.time,
    fim,
    duracaoMin: row.durationMinutes ?? 0,
    descricao: (idioma === 'en'
      ? { 'Vento forte': 'Winds above 30 kt forecast in the area.', 'Trovoada': 'Thunderstorm cells on approach.', 'Neblina': 'Reduced visibility due to fog.', 'Rajada': 'Wind gusts on the runway.' }
      : { 'Vento forte': 'Ventos superiores a 30 nós previstos na área.', 'Trovoada': 'Células de trovoada na aproximação.', 'Neblina': 'Visibilidade reduzida por neblina.', 'Rajada': 'Rajadas de vento na pista.' })[row.alerta] || '',
    nivel,
    equipamentos: '',
  };
}

/** Converte voo da API em data ISO para Date */
function parseFlightTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso);
  } catch {
    return null;
  }
}

/** Calcula status da aeronave na régua */
function computeStatus(flight, timeDate) {
  const now = new Date();
  const mAway = (timeDate - now) / 60000;
  if (flight.flight_type === 'arrival' && flight.real_arrival) return 'past';
  if (flight.flight_type === 'departure' && flight.real_departure) return 'past';
  if (mAway < -2) return 'past';
  if (mAway >= -2 && mAway <= 5) return 'now';
  if (mAway > 5 && mAway <= 30) return 'soon';
  return 'ok';
}

/** Converte alertas da API em rows para a régua de alertas.
 * @param {Array} alerts - Lista de alertas da API
 * @param {Date} [nowRef] - Referência "agora" da régua (tempo real ou tempo do cenário); se omitido usa new Date()
 */
function alertsToRows(alerts, nowRef) {
  if (!Array.isArray(alerts) || alerts.length === 0) return [];
  const now = nowRef instanceof Date ? nowRef : new Date();
  const rows = [];
  for (const a of alerts) {
    const timeDate = (a.date_start ? new Date(a.date_start) : null) || (a.date_insert ? new Date(a.date_insert) : null) || new Date();
    const mAway = (timeDate - now) / 60000;
    if (mAway < -PAST_MINUTES - 1 || mAway > FUTURE_MINUTES + 1) continue;
    let status = 'ok';
    if (mAway < -2) status = 'past';
    else if (mAway >= -2 && mAway <= 5) status = 'now';
    else if (mAway > 5 && mAway <= 30) status = 'soon';
    rows.push({
      id: `alerta-${a.id}`,
      alerta: semTravessao(a.title || a.name),
      alertaEn: semTravessao(a.title_en || a.name_en || a.title || a.name),
      aeronave: '',
      time: timeDate,
      status,
      durationMinutes: a.period ?? 30,
      flight: null,
      alertFromApi: true,
      alertData: a,
    });
  }
  return rows;
}

/** Converte voos da API em rows para a régua.
 * @param {Array} flights - Lista de voos da API
 * @param {Date} [nowRef] - Referência "agora" da régua; se omitido usa new Date()
 */
function flightsToRows(flights, nowRef) {
  if (!Array.isArray(flights) || flights.length === 0) return [];
  const now = nowRef instanceof Date ? nowRef : new Date();
  const rows = [];
  for (const f of flights) {
    const timeStr = f.flight_type === 'arrival'
      ? (f.estimated_arrival || f.scheduled_arrival || f.eta)
      : (f.estimated_departure || f.scheduled_departure);
    const timeDate = parseFlightTime(timeStr);
    if (!timeDate) continue;
    const mAway = (timeDate - now) / 60000;
    if (mAway < -PAST_MINUTES - 1 || mAway > FUTURE_MINUTES + 1) continue; // fora da janela
    const status = computeStatus(f, timeDate);
    const alerta = f.alert_level && f.alert_level !== 'none' && f.alert_message ? semTravessao(f.alert_message) : '';
    rows.push({
      id: f.id ?? `f-${f.callsign}-${f.flight_date}-${f.flight_type}`,
      alerta,
      aeronave: f.callsign,
      time: timeDate,
      status,
      durationMinutes: 0,
      flight: f,
    });
  }
  return rows.sort((a, b) => a.time - b.time);
}

function getDetalhesAeronave(row, idioma = 'pt') {
  const f = row.flight;
  if (f) {
    const timeDate = f.flight_type === 'arrival'
      ? parseFlightTime(f.estimated_arrival || f.scheduled_arrival || f.eta)
      : parseFlightTime(f.estimated_departure || f.scheduled_departure);
    const origem = f.flight_type === 'arrival' ? (f.origin_iata || f.origin_icao || '—') : 'LPFR';
    const destino = f.flight_type === 'departure' ? (f.destination_iata || f.destination_icao || '—') : 'LPFR';
    const tipo = f.aircraft_model_text || f.aircraft_model_code || '—';
    let status = f.status || '—';
    if (row.status === 'past') {
      status = idioma === 'en' ? (f.flight_type === 'arrival' ? 'Landed' : 'Departed') : (f.flight_type === 'arrival' ? 'Pousou' : 'Partiu');
    }
    return {
      callsign: f.callsign,
      origem,
      destino,
      eta: timeDate || row.time,
      etaLabel: f.flight_type === 'arrival' ? 'ETA' : 'ETD',
      tipo,
      status,
      flight: f,
    };
  }
  const mAway = (row.time - new Date()) / 60000;
  return {
    callsign: row.aeronave,
    origem: '—',
    destino: '—',
    eta: row.time,
    etaLabel: 'ETA',
    tipo: '—',
    status: row.status === 'past' ? 'Pousou/Partiu' : mAway <= 5 ? 'Aproximando' : 'Em rota',
  };
}

/**
 * Calcula topY de cada item pela posição no tempo (sem empilhar para baixo).
 * Cada etiqueta fica na altura que corresponde ao seu horário na régua.
 * @param {Array} rows - Lista de rows (flight ou alert)
 * @param {Date} now - Data/hora atual
 * @param {(row) => number} getHeight - Função que retorna altura em px da etiqueta
 * @returns {Array<{ row, topY: number }>}
 */
function computeStackedPositions(rows, now, getHeight) {
  return rows
    .map((row) => {
      const mAway = (row.time - now) / 60000;
      const topY = NOW_Y + mAway * PX_PER_MINUTE;
      return { row, topY };
    })
    .filter(({ topY }) => topY >= -100 && topY <= TOTAL_PX + 100)
    .sort((a, b) => a.topY - b.topY);
}

/** Verifica se dois intervalos [topA,bottomA] e [topB,bottomB] se intersectam */
function intervalsOverlap(topA, bottomA, topB, bottomB) {
  return topA < bottomB && topB < bottomA;
}

/**
 * Aplica lógica de até 6 camadas sobrepostas aos alertas.
 * Até 6 alertas sobrepostos: diminuem largura progressivamente (camadas 1–6).
 * Acima de 6: empilham verticalmente abaixo.
 * @param {Array<{ row, topY }>} positionedAlerts - Alertas com posição base
 * @param {(row) => number} getHeight - Função que retorna altura em px
 * @returns {Array<{ row, topY: number, overlapLayer: number }>} overlapLayer: 1–6 para sobreposição, 0 para empilhado
 */
function computeAlertOverlapLayers(positionedAlerts, getHeight) {
  if (positionedAlerts.length === 0) return [];
  const items = positionedAlerts.map(({ row, topY }) => {
    const h = getHeight(row);
    return { row, topY, bottomY: topY + h, height: h };
  });

  const result = [];
  const assigned = new Set();

  for (let i = 0; i < items.length; i++) {
    if (assigned.has(i)) continue;

    const group = [i];
    assigned.add(i);
    let changed = true;
    while (changed) {
      changed = false;
      for (let j = 0; j < items.length; j++) {
        if (assigned.has(j)) continue;
        const b = items[j];
        const overlapsAny = group.some((idx) => {
          const a = items[idx];
          return intervalsOverlap(a.topY, a.bottomY, b.topY, b.bottomY);
        });
        if (overlapsAny) {
          group.push(j);
          assigned.add(j);
          changed = true;
        }
      }
    }

    group.sort((a, b) => items[a].topY - items[b].topY);
    let stackBottomY = 0;

    for (let k = 0; k < group.length; k++) {
      const idx = group[k];
      const item = items[idx];
      if (k < MAX_OVERLAP_LAYERS) {
        result.push({ row: item.row, topY: item.topY, overlapLayer: k + 1 });
        stackBottomY = Math.max(stackBottomY, item.topY + item.height);
      } else {
        const adjustedTopY = stackBottomY + ETIQUETA_STACK_GAP_PX;
        result.push({ row: item.row, topY: adjustedTopY, overlapLayer: 0 });
        stackBottomY = adjustedTopY + item.height;
      }
    }
  }

  return result.sort((a, b) => a.topY - b.topY);
}

/** Altura fixa das etiquetas de voo (px) — usada para resolver sobreposições */
const FLIGHT_ETIQUETA_HEIGHT_PX = 32;

/**
 * Ajusta topY das etiquetas de voo para não se sobreporem verticalmente.
 * Empilha para baixo com ETIQUETA_STACK_GAP_PX de separação.
 */
function resolveFlightOverlaps(positionedRows) {
  if (positionedRows.length === 0) return positionedRows;
  const half = FLIGHT_ETIQUETA_HEIGHT_PX / 2;
  const result = positionedRows.map(({ row, topY }) => ({ row, topY }));
  for (let i = 1; i < result.length; i++) {
    const prevBottom = result[i - 1].topY + half;
    const currTop = result[i].topY - half;
    if (currTop < prevBottom + ETIQUETA_STACK_GAP_PX) {
      result[i].topY = prevBottom + ETIQUETA_STACK_GAP_PX + half;
    }
  }
  return result;
}

export default function SidebarTimeline({ referenceTime = null, alertas = [], voos = [], idioma = 'pt' }) {
  // Textos da interface conforme o idioma selecionado
  const TXT = idioma === 'en'
    ? {
        timeline: 'Timeline',
        alerts: 'Alerts',
        flights: 'Flights',
        locked: 'Locked to now',
        backNow: 'Back to now',
        flightAlert: 'Flight coincides with weather alert',
        close: 'Close',
        alertDetails: 'Alert details',
        aircraftDetails: 'Aircraft details',
        alertLabel: 'Alert',
        start: 'Start (UTC)',
        end: 'End (UTC)',
        duration: 'Duration',
        level: 'Level',
        description: 'Description',
        equipment: 'Equipment',
        route: 'Route',
        type: 'Type',
        status: 'Status',
        now: 'NOW',
      }
    : {
        timeline: 'Linha do tempo',
        now: 'AGORA',
        alerts: 'Alertas',
        flights: 'Voos',
        locked: 'Travado no agora',
        backNow: 'Voltar ao agora',
        flightAlert: 'Voo coincide com alerta meteorológico',
        close: 'Fechar',
        alertDetails: 'Detalhes do alerta',
        aircraftDetails: 'Detalhes da aeronave',
        alertLabel: 'Alerta',
        start: 'Início (UTC)',
        end: 'Fim (UTC)',
        duration: 'Duração',
        level: 'Nível',
        description: 'Descrição',
        equipment: 'Equipamentos',
        route: 'Rota',
        type: 'Tipo',
        status: 'Status',
      };

  const [now, setNow] = useState(new Date());
  const [locked, setLocked] = useState(true);
  const referenceTimeRef = useRef(null);
  const startedAtRef = useRef(Date.now());
  const alerts = alertas;
  const flights = voos;
  const alertRows = useMemo(() => alertsToRows(alerts, now), [alerts, now]);
  const flightRows = useMemo(() => flightsToRows(flights, now), [flights, now]);
  const getAlertHeight = (row) => Math.max(MIN_ETIQUETA_HEIGHT_PX, (row.durationMinutes ?? 0) * PX_PER_MINUTE);
  const alertRowsWithPosition = useMemo(
    () =>
      computeAlertOverlapLayers(
        computeStackedPositions(alertRows, now, getAlertHeight),
        getAlertHeight
      ),
    [alertRows, now]
  );
  const flightRowsWithPosition = useMemo(
    () => resolveFlightOverlaps(
      computeStackedPositions(flightRows, now, () => MIN_ETIQUETA_HEIGHT_PX)
    ),
    [flightRows, now]
  );
  const [detalhe, setDetalhe] = useState(null); // { type: 'alerta' | 'aeronave', row }
  const scrollRef = useRef(null);
  const initDone = useRef(false);
  const lockingRef = useRef(true);

  // Quando o tempo selecionado no menu de baixo muda, fixar o início da contagem e saltar a régua para esse momento
  useEffect(() => {
    referenceTimeRef.current = referenceTime;
    if (referenceTime) {
      startedAtRef.current = Date.now();
      setNow(new Date(referenceTime));
    } else {
      setNow(new Date());
    }
  }, [referenceTime]);

  // Atualizar "agora" a cada segundo: tempo real ou tempo de referência + segundos decorridos
  useEffect(() => {
    const id = setInterval(() => {
      if (referenceTimeRef.current) {
        const refDate = new Date(referenceTimeRef.current);
        const elapsed = Date.now() - startedAtRef.current;
        setNow(new Date(refDate.getTime() + elapsed));
      } else {
        setNow(new Date());
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (scrollRef.current && !initDone.current) {
      initDone.current = true;
      scrollRef.current.scrollTop = NOW_Y - 60;
    }
  }, []);

  useEffect(() => {
    if (locked && scrollRef.current) {
      lockingRef.current = true;
      scrollRef.current.scrollTop = NOW_Y - 60;
      setTimeout(() => {
        lockingRef.current = false;
      }, 50);
    }
  }, [now, locked]);

  const scrollBy5min = useCallback((dir) => {
    setLocked(false);
    scrollRef.current?.scrollBy({ top: dir * 5 * PX_PER_MINUTE, behavior: 'smooth' });
  }, []);

  const goToNow = useCallback(() => {
    setLocked(true);
  }, []);

  const handleWheel = useCallback(() => {
    if (!lockingRef.current) setLocked(false);
  }, []);

  const ticks = buildTicks(now);
  const openDetalheAlerta = (e, row) => {
    e.stopPropagation();
    if (row.alerta) setDetalhe({ type: 'alerta', row });
  };
  const openDetalheAeronave = (e, row) => {
    e.stopPropagation();
    setDetalhe({ type: 'aeronave', row });
  };
  const closeDetalhe = () => setDetalhe(null);

  return (
    <div className="sidebar-timeline">
      <div className="sidebar-timeline-header">
        <span className="sidebar-timeline-title">{TXT.timeline}</span>
      </div>

      <div className="sidebar-timeline-body">
        <div className="sidebar-timeline-controls">
          <button
            type="button"
            className="sidebar-timeline-btn"
            onClick={() => scrollBy5min(-1)}
            title="−5 min"
          >
            <LuChevronUp size={14} />
          </button>
          <button
            type="button"
            className={`sidebar-timeline-lock ${locked ? 'active' : ''}`}
            onClick={goToNow}
            title={locked ? TXT.locked : TXT.backNow}
          >
            {locked ? <LuLock size={14} /> : <LuArrowUpToLine size={14} />}
          </button>
          <button
            type="button"
            className="sidebar-timeline-btn"
            onClick={() => scrollBy5min(1)}
            title="+5 min"
          >
            <LuChevronDown size={14} />
          </button>
        </div>

        <div
          className="sidebar-timeline-scroll"
          ref={scrollRef}
          onWheel={handleWheel}
          onTouchMove={handleWheel}
        >
          <div className="sidebar-timeline-inner" style={{ height: TOTAL_PX }}>
            {/* Coluna esquerda: etiquetas de alertas */}
            <div className="sidebar-timeline-col sidebar-timeline-alertas">
              <div
                className="sidebar-timeline-col-title sidebar-timeline-col-title-alertas"
              >
                {TXT.alerts}
              </div>
              {alertRowsWithPosition.map(({ row, topY, overlapLayer }) => {
                const mAway = (row.time - now) / 60000;
                if (mAway < -PAST_MINUTES - 1 || mAway > FUTURE_MINUTES + 1) return null;
                const durationMin = row.durationMinutes ?? 0;
                const heightPx = Math.max(MIN_ETIQUETA_HEIGHT_PX, durationMin * PX_PER_MINUTE);
                const color = riskColor(row.alertData?.risk);
                const isOverlapLayer = overlapLayer >= 1 && overlapLayer <= MAX_OVERLAP_LAYERS;
                const leftOffset = isOverlapLayer
                  ? 6 + (overlapLayer - 1) * ETIQUETA_OVERLAP_OFFSET_PX
                  : 6;
                return (
                  <div
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    className={`sidebar-timeline-etiqueta sidebar-timeline-etiqueta-alerta ${isOverlapLayer ? 'sidebar-timeline-etiqueta-alerta-overlap' : ''}`}
                    style={{
                      top: topY,
                      height: heightPx,
                      minHeight: MIN_ETIQUETA_HEIGHT_PX,
                      borderLeftColor: color,
                      left: leftOffset,
                      right: 6,
                      zIndex: 5 + overlapLayer,
                    }}
                    onClick={(e) => openDetalheAlerta(e, row)}
                    onKeyDown={(e) => e.key === 'Enter' && openDetalheAlerta(e, row)}
                  >
                    {idioma === 'en' ? (row.alertaEn || row.alerta) : row.alerta}
                  </div>
                );
              })}
            </div>

            {/* Coluna centro: régua (eixo de tempo + linha AGORA) */}
            <div className="sidebar-timeline-col sidebar-timeline-regua">
              <div className="sidebar-timeline-axis">
                {ticks.map(({ m, label, y }) => (
                  <div key={m} className="sidebar-timeline-tick" style={{ top: y }}>
                    <span
                      className="sidebar-timeline-tick-label"
                      style={{
                        color: m === 0 ? '#00e5ff' : m < 0 ? '#253b55' : '#3a5580',
                        fontWeight: m === 0 ? 700 : 400,
                      }}
                    >
                      {label}
                    </span>
                    <div className={`sidebar-timeline-tick-line ${m % 30 === 0 ? 'major' : 'minor'}`} />
                  </div>
                ))}
              </div>
              <div className="sidebar-timeline-canvas">
                <div className="sidebar-timeline-past" style={{ height: NOW_Y }} />
                <div className="sidebar-timeline-now" style={{ top: NOW_Y }}>
                  <span className="sidebar-timeline-now-badge">{TXT.now}</span>
                </div>
              </div>
            </div>

            {/* Coluna direita: etiquetas de aeronaves (dados da API de voos) */}
            <div className="sidebar-timeline-col sidebar-timeline-aeronaves">
              <div className="sidebar-timeline-col-title">{TXT.flights}</div>
              {flightRowsWithPosition.map(({ row, topY }) => {
                const mAway = (row.time - now) / 60000;
                if (mAway < -PAST_MINUTES - 1 || mAway > FUTURE_MINUTES + 1) return null;
                const color = statusColor(row.status);
                const isArrival = row.flight?.flight_type === 'arrival';
                const FlightIcon = isArrival ? LuPlaneLanding : LuPlaneTakeoff;
                const flightIconTitle = isArrival ? 'Pouso (LPFR destino)' : 'Decolagem (LPFR origem)';
                const alertIndicatorColor = flightAlertIndicatorColor(row.time, alertRows);
                return (
                  <div
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    className="sidebar-timeline-etiqueta sidebar-timeline-etiqueta-aeronave"
                    style={{
                      top: topY,
                      borderLeftColor: color,
                    }}
                    onClick={(e) => openDetalheAeronave(e, row)}
                    onKeyDown={(e) => e.key === 'Enter' && openDetalheAeronave(e, row)}
                  >
                    <span className="sidebar-timeline-etiqueta-aeronave-label">{row.aeronave}</span>
                    {alertIndicatorColor && (
                      <span
                        className="sidebar-timeline-etiqueta-flight-alert-icon"
                        style={{ color: alertIndicatorColor }}
                        title={TXT.flightAlert}
                        aria-hidden="true"
                      >
                        <LuTriangleAlert size={16} />
                      </span>
                    )}
                    <span className="sidebar-timeline-etiqueta-flight-icon" title={flightIconTitle} aria-hidden="true">
                      <FlightIcon size={16} />
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Janela de detalhes — renderizada no body */}
      {detalhe &&
        createPortal(
          <div className="sidebar-timeline-detalhe-backdrop" onClick={closeDetalhe} aria-hidden="true">
            <div
              className="sidebar-timeline-detalhe"
              style={detalhe.type === 'alerta' ? { borderTopColor: riskColor(detalhe.row.alertData?.risk) } : undefined}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sidebar-timeline-detalhe-header">
                <h3 className="sidebar-timeline-detalhe-title">
                  {detalhe.type === 'alerta' ? TXT.alertDetails : TXT.aircraftDetails}
                </h3>
                <button type="button" className="sidebar-timeline-detalhe-close" onClick={closeDetalhe} aria-label={TXT.close}>
                  <LuX size={16} />
                </button>
              </div>
              <div className="sidebar-timeline-detalhe-body">
                {detalhe.type === 'alerta' ? (
                  (() => {
                    const d = getDetalhesAlerta(detalhe.row, undefined, idioma);
                    return (
                      <>
                        <div className="sidebar-timeline-detalhe-field sidebar-timeline-detalhe-field-full">
                          <span className="sidebar-timeline-detalhe-label">{TXT.alertLabel}</span>
                          <span className="sidebar-timeline-detalhe-value">{d.titulo}</span>
                        </div>
                        <div className="sidebar-timeline-detalhe-field">
                          <span className="sidebar-timeline-detalhe-label">{TXT.start}</span>
                          <span className="sidebar-timeline-detalhe-value">{fmtTime(d.inicio)}</span>
                        </div>
                        <div className="sidebar-timeline-detalhe-field">
                          <span className="sidebar-timeline-detalhe-label">{TXT.end}</span>
                          <span className="sidebar-timeline-detalhe-value">{fmtTime(d.fim)}</span>
                        </div>
                        <div className="sidebar-timeline-detalhe-field">
                          <span className="sidebar-timeline-detalhe-label">{TXT.duration}</span>
                          <span className="sidebar-timeline-detalhe-value">{d.duracaoMin} min</span>
                        </div>
                        <div className="sidebar-timeline-detalhe-field">
                          <span className="sidebar-timeline-detalhe-label">{TXT.level}</span>
                          <span className="sidebar-timeline-detalhe-value">{d.nivel}</span>
                        </div>
                        {d.descricao && (
                          <div className="sidebar-timeline-detalhe-field sidebar-timeline-detalhe-field-full">
                            <span className="sidebar-timeline-detalhe-label">{TXT.description}</span>
                            <span className="sidebar-timeline-detalhe-value">{d.descricao}</span>
                          </div>
                        )}
                        {d.equipamentos && (
                          <div className="sidebar-timeline-detalhe-field sidebar-timeline-detalhe-field-full">
                            <span className="sidebar-timeline-detalhe-label">{TXT.equipment}</span>
                            <span className="sidebar-timeline-detalhe-value">{d.equipamentos}</span>
                          </div>
                        )}
                      </>
                    );
                  })()
                ) : (
                  (() => {
                    const d = getDetalhesAeronave(detalhe.row, idioma);
                    return (
                      <>
                        <div className="sidebar-timeline-detalhe-field">
                          <span className="sidebar-timeline-detalhe-label">Callsign</span>
                          <span className="sidebar-timeline-detalhe-value">{d.callsign}</span>
                        </div>
                        <div className="sidebar-timeline-detalhe-field sidebar-timeline-detalhe-field-full">
                          <span className="sidebar-timeline-detalhe-label">{TXT.route}</span>
                          <span className="sidebar-timeline-detalhe-value">{d.origem} → {d.destino}</span>
                        </div>
                        <div className="sidebar-timeline-detalhe-field">
                          <span className="sidebar-timeline-detalhe-label">{d.etaLabel || 'ETA'}</span>
                          <span className="sidebar-timeline-detalhe-value">{d.eta ? fmtTime(d.eta) : '—'}</span>
                        </div>
                        <div className="sidebar-timeline-detalhe-field">
                          <span className="sidebar-timeline-detalhe-label">{TXT.type}</span>
                          <span className="sidebar-timeline-detalhe-value">{d.tipo}</span>
                        </div>
                        <div className="sidebar-timeline-detalhe-field">
                          <span className="sidebar-timeline-detalhe-label">{TXT.status}</span>
                          <span className="sidebar-timeline-detalhe-value">{d.status}</span>
                        </div>
                      </>
                    );
                  })()
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

    </div>
  );
}
